import "./style.scss";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ClipLoader } from "react-spinners";
import { Copy, Dislike, Like1, Play, Share } from "iconsax-react";
import { GlobalStateContext } from "../../layouts/GlobalStates";
import { DBButton } from "../../DBComponents/DBButton";
import DBTextArea from "../../component/global/DBTextArea";
import {
    RewardBadge,
    RewardStatus,
} from "../../features/incentives/Rewards";
import MediaPreview from "../../features/content-studio/MediaPreview";
import "../../features/content-studio/styles.scss";
import { TYPE_KEY } from "../../features/content-studio/constants";
import { formatCount, thumbnailUrlFor } from "../../features/content-studio/helpers";
import { contentStudioApi, errorMessage, publicContentShareUrl } from "../../features/content-studio/api";
import { copyToClipboard } from "../helperFunctions";
import { itemContentsUrl } from "../universalData";
import FeedbackAuthModal from "../SharedComponents/AuthModal";
import ShareSmsModal from "./ShareSmsModal";

const nextCounts = (content, nextVote) => {
    const likes = Number(content.likes_count || 0);
    const dislikes = Number(content.dislikes_count || 0);
    const current = content.my_vote;
    if (current === nextVote) {
        return {
            likes_count: nextVote === "like" ? Math.max(0, likes - 1) : likes,
            dislikes_count:
                nextVote === "dislike" ? Math.max(0, dislikes - 1) : dislikes,
            my_vote: null,
        };
    }
    return {
        likes_count:
            nextVote === "like"
                ? likes + 1
                : current === "like"
                ? Math.max(0, likes - 1)
                : likes,
        dislikes_count:
            nextVote === "dislike"
                ? dislikes + 1
                : current === "dislike"
                ? Math.max(0, dislikes - 1)
                : dislikes,
        my_vote: nextVote,
    };
};

const hasSession = () => Boolean(sessionStorage.getItem("auth"));

export default function Contents() {
    const { t, i18n } = useTranslation();
    const { feedbackAppGlobalState } = useContext(GlobalStateContext);
    const [result, setResult] = feedbackAppGlobalState;
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [playingId, setPlayingId] = useState(null);
    const [description, setDescription] = useState("");
    const [url, setUrl] = useState("");
    const [shareCode, setShareCode] = useState("");
    const [shareId, setShareId] = useState(null);
    const [copied, setCopied] = useState(false);
    const [linkBusy, setLinkBusy] = useState(false);
    const [smsBusy, setSmsBusy] = useState(false);
    const [shareError, setShareError] = useState("");
    const [smsError, setSmsError] = useState("");
    const [showSms, setShowSms] = useState(false);
    const [smsSent, setSmsSent] = useState(false);
    const [voteBusy, setVoteBusy] = useState(null);
    const [showAuth, setShowAuth] = useState(false);
    const pendingRef = useRef(null);
    const linkPromiseRef = useRef(null);
    const locale = i18n.language === "fa" ? "fa-IR" : "en-US";
    const itemId = result.data?.item;

    const loadContents = (silent = false) => {
        if (!itemId) {
            setContents([]);
            setLoading(false);
            return Promise.resolve([]);
        }
        if (!silent) setLoading(true);
        return axios
            .get(itemContentsUrl(itemId), {
                headers: sessionStorage.getItem("auth")
                    ? { Authorization: `Bearer ${sessionStorage.getItem("auth")}` }
                    : undefined,
            })
            .then((response) => {
                const rows = Array.isArray(response.data) ? response.data : [];
                setContents(rows);
                return rows;
            })
            .catch(() => {
                setContents([]);
                return [];
            })
            .finally(() => {
                if (!silent) setLoading(false);
            });
    };

    useEffect(() => {
        let cancelled = false;
        if (!itemId) {
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        axios
            .get(itemContentsUrl(itemId), {
                headers: sessionStorage.getItem("auth")
                    ? { Authorization: `Bearer ${sessionStorage.getItem("auth")}` }
                    : undefined,
            })
            .then((response) => {
                if (!cancelled) {
                    setContents(
                        Array.isArray(response.data) ? response.data : []
                    );
                }
            })
            .catch(() => {
                if (!cancelled) setContents([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [itemId]);

    const requireAuth = (pending) => {
        if (hasSession()) return true;
        pendingRef.current = pending;
        setShowAuth(true);
        return false;
    };

    const patchContent = (contentId, patch) => {
        setContents((current) =>
            current.map((row) =>
                row.id === contentId ? { ...row, ...patch } : row
            )
        );
    };

    const vote = (content, nextVote) => {
        if (!requireAuth({ kind: "vote", content, nextVote })) return;
        if (voteBusy === content.id) return;
        const previous = {
            likes_count: content.likes_count,
            dislikes_count: content.dislikes_count,
            my_vote: content.my_vote,
        };
        const optimistic = nextCounts(content, nextVote);
        patchContent(content.id, optimistic);
        setVoteBusy(content.id);
        const request =
            content.my_vote === nextVote
                ? contentStudioApi.unlike(content.id)
                : nextVote === "like"
                ? contentStudioApi.like(content.id)
                : contentStudioApi.dislike(content.id);
        request
            .catch((error) => {
                patchContent(content.id, previous);
                if (error?.response?.status === 401) {
                    pendingRef.current = { kind: "vote", content, nextVote };
                    setShowAuth(true);
                }
            })
            .finally(() => setVoteBusy(null));
    };

    const shareSenderPhone = () => {
        let phone = String(
            result.username || window.sessionStorage.getItem("username") || ""
        )
            .replace(/[۰-۹]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
            .replace(/[٠-٩]/g, (digit) => "٠١٢٣٤٥٦٧٨٩".indexOf(digit))
            .replace(/[\s-]/g, "");
        if (phone.startsWith("+98")) {
            phone = `0${phone.slice(3)}`;
        } else if (phone.startsWith("0098")) {
            phone = `0${phone.slice(4)}`;
        } else if (/^98\d{10}$/.test(phone)) {
            phone = `0${phone.slice(2)}`;
        } else if (/^9\d{9}$/.test(phone)) {
            phone = `0${phone}`;
        }
        return /^09\d{9}$/.test(phone) ? phone : "";
    };

    const defaultMessage = (content) => {
        const user = shareSenderPhone();
        const item = content.name || "";
        const body = user
            ? t("contents.share.msg", { user, item })
            : t("contents.share.msg.noUser", { item });
        return (
            body +
            "\n\n" +
            t("start.rating.thanks") +
            "\n" +
            t("start.rating.didban")
        );
    };

    const resolveShareUrl = (payload) =>
        payload?.url || publicContentShareUrl(payload.code);

    const smsPreview = (content) => {
        const body = description.trim() || defaultMessage(content);
        if (!url) return body;
        return body.includes(url) ? body : `${body}\n\n${url}`;
    };

    const createShareLink = (content, note) => {
        if (linkPromiseRef.current) return linkPromiseRef.current;
        setLinkBusy(true);
        setShareError("");
        const text = (note ?? description).trim();
        linkPromiseRef.current = contentStudioApi
            .createShareLink({
                content: content.id,
                description: text,
            })
            .then((payload) => {
                const link = resolveShareUrl(payload);
                setShareCode(payload.code);
                setShareId(payload.id);
                setUrl(link);
                return { code: payload.code, url: link, id: payload.id };
            })
            .catch((error) => {
                setShareError(errorMessage(error, t("contents.share.error")));
                throw error;
            })
            .finally(() => {
                linkPromiseRef.current = null;
                setLinkBusy(false);
            });
        return linkPromiseRef.current;
    };

    const ensureShareLink = (content) => {
        if (shareCode && url) {
            return Promise.resolve({ code: shareCode, url, id: shareId });
        }
        return createShareLink(content);
    };

    const persistShareNote = (payload) => {
        const note = description.trim();
        if (!payload?.id || !note) return Promise.resolve(payload);
        return contentStudioApi
            .updateShareLink(payload.id, { description: note })
            .then(() => payload)
            .catch(() => payload);
    };

    const copyLink = (content) => {
        if (!requireAuth({ kind: "share", content })) return;
        ensureShareLink(content)
            .then(persistShareNote)
            .then((payload) => {
                copyToClipboard(payload.url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1000);
            })
            .catch(() => undefined);
    };

    const markCopied = () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
    };

    const shareNative = (content) => {
        if (!requireAuth({ kind: "share", content })) return;
        if (!url) return;
        persistShareNote({ id: shareId, url, code: shareCode });
        const text = description.trim() || defaultMessage(content);
        if (typeof navigator.share !== "function") {
            copyToClipboard(url);
            markCopied();
            return;
        }
        navigator
            .share({
                title: content.name,
                text,
                url,
            })
            .catch((error) => {
                if (error?.name === "AbortError") return;
                copyToClipboard(url);
                markCopied();
            });
    };

    const openSms = (content) => {
        if (!requireAuth({ kind: "sms", content })) return;
        ensureShareLink(content)
            .then(() => {
                setSmsError("");
                setShowSms(true);
            })
            .catch(() => undefined);
    };

    const sendSms = (content, phones) => {
        setSmsBusy(true);
        setSmsError("");
        ensureShareLink(content)
            .then(persistShareNote)
            .then((payload) =>
                contentStudioApi.sendShareSms(payload.code, {
                    phones,
                    message: smsPreview(content),
                })
            )
            .then(() => {
                setSmsSent(true);
                setShowSms(false);
            })
            .catch((error) => {
                setSmsError(
                    errorMessage(error, t("contents.share.sms.error"))
                );
            })
            .finally(() => setSmsBusy(false));
    };

    const openShare = (content, options = {}) => {
        if (!requireAuth({ kind: options.sms ? "sms" : "share", content })) {
            return;
        }
        setOpenId(content.id);
        setDescription("");
        setUrl("");
        setShareCode("");
        setShareId(null);
        setShareError("");
        setSmsSent(false);
        linkPromiseRef.current = null;
        createShareLink(content, "")
            .then(() => {
                if (options.sms) {
                    setSmsError("");
                    setShowSms(true);
                }
            })
            .catch(() => undefined);
    };

    const handleAuthSuccess = (response) => {
        setResult((current) => ({
            ...current,
            username: response?.user?.username || current.username,
        }));
        setShowAuth(false);
        const pending = pendingRef.current;
        pendingRef.current = null;
        loadContents(true).then((rows) => {
            if (pending?.kind === "vote") {
                const fresh =
                    rows.find((row) => row.id === pending.content.id) ||
                    pending.content;
                vote(fresh, pending.nextVote);
            } else if (pending?.kind === "share" || pending?.kind === "sms") {
                const fresh =
                    rows.find((row) => row.id === pending.content.id) ||
                    pending.content;
                openShare(fresh, { sms: pending.kind === "sms" });
            }
        });
    };

    return (
        <div className="main-container">
            <div className="participation-reward-row">
                <RewardBadge
                    action="share"
                    targetType="item"
                    targetId={result.data?.item}
                    opportunity={
                        result.ajax?.incentive_opportunities?.share
                    }
                />
            </div>
            <div className="contents-page">
                <header className="contents-page__intro">
                    <h2>{t("contents.title")}</h2>
                    <p>{t("contents.lead")}</p>
                </header>
                {url ? (
                    <RewardStatus
                        action="share"
                        targetType="item"
                        targetId={result.data?.item}
                    />
                ) : null}
                {loading ? (
                    <div className="contents-page__loading">
                        <ClipLoader color="#1e3a8a" size={28} />
                    </div>
                ) : contents.length === 0 ? (
                    <p className="contents-page__empty">{t("contents.empty")}</p>
                ) : (
                    <ul className="contents-page__list">
                        {contents.map((content) => {
                            const type =
                                TYPE_KEY[content.content_type] || "text";
                            const isOpen = openId === content.id;
                            const isPlaying = playingId === content.id;
                            const thumb = thumbnailUrlFor(content);
                            return (
                                <li key={content.id} className="contents-card">
                                    <div className="contents-card__media">
                                        {isPlaying ? (
                                            <MediaPreview content={content} />
                                        ) : thumb ? (
                                            <img src={thumb} alt="" />
                                        ) : (
                                            <MediaPreview content={content} />
                                        )}
                                        {!isPlaying ? (
                                            <button
                                                type="button"
                                                className="contents-card__play"
                                                onClick={() =>
                                                    setPlayingId(content.id)
                                                }
                                                aria-label={t("contents.play")}
                                            >
                                                <Play
                                                    size={22}
                                                    variant="Bold"
                                                />
                                            </button>
                                        ) : null}
                                        <span className="contents-card__type">
                                            {t(`contents.type.${type}`)}
                                        </span>
                                    </div>
                                    <div className="contents-card__body">
                                        <h3>{content.name}</h3>
                                        {content.description ? (
                                            <p>{content.description}</p>
                                        ) : null}
                                        <div className="contents-card__toolbar">
                                            <button
                                                type="button"
                                                className={
                                                    content.my_vote === "like"
                                                        ? "is-on"
                                                        : ""
                                                }
                                                aria-pressed={
                                                    content.my_vote === "like"
                                                }
                                                aria-label={t("contents.like")}
                                                disabled={
                                                    voteBusy === content.id
                                                }
                                                onClick={() =>
                                                    vote(content, "like")
                                                }
                                            >
                                                <Like1
                                                    size={18}
                                                    variant={
                                                        content.my_vote ===
                                                        "like"
                                                            ? "Bold"
                                                            : "Linear"
                                                    }
                                                />
                                                <span>
                                                    {formatCount(
                                                        content.likes_count,
                                                        locale
                                                    )}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={
                                                    content.my_vote ===
                                                    "dislike"
                                                        ? "is-on is-down"
                                                        : ""
                                                }
                                                aria-pressed={
                                                    content.my_vote ===
                                                    "dislike"
                                                }
                                                aria-label={t(
                                                    "contents.dislike"
                                                )}
                                                disabled={
                                                    voteBusy === content.id
                                                }
                                                onClick={() =>
                                                    vote(content, "dislike")
                                                }
                                            >
                                                <Dislike
                                                    size={18}
                                                    variant={
                                                        content.my_vote ===
                                                        "dislike"
                                                            ? "Bold"
                                                            : "Linear"
                                                    }
                                                />
                                                <span>
                                                    {formatCount(
                                                        content.dislikes_count,
                                                        locale
                                                    )}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={
                                                    isOpen ? "is-on" : ""
                                                }
                                                aria-expanded={isOpen}
                                                aria-label={t(
                                                    "contents.share.action"
                                                )}
                                                onClick={() =>
                                                    isOpen
                                                        ? setOpenId(null)
                                                        : openShare(content)
                                                }
                                            >
                                                <Share size={18} />
                                                <span>
                                                    {t("contents.share.action")}
                                                </span>
                                            </button>
                                        </div>
                                        {isOpen ? (
                                            <div className="contents-card__share">
                                                <p>
                                                    {t("contents.share.hint")}
                                                </p>
                                                <DBTextArea
                                                    value={description}
                                                    onChange={(event) =>
                                                        setDescription(
                                                            event.target.value
                                                        )
                                                    }
                                                    placeholder={defaultMessage(
                                                        content
                                                    )}
                                                />
                                                {shareError ? (
                                                    <p
                                                        className="contents-card__share-error"
                                                        role="alert"
                                                    >
                                                        {shareError}
                                                    </p>
                                                ) : null}
                                                {smsSent ? (
                                                    <p className="contents-card__share-ok">
                                                        {t(
                                                            "contents.share.sms.sent"
                                                        )}
                                                    </p>
                                                ) : null}
                                                <DBButton
                                                    loading={linkBusy && !url}
                                                    disabled={linkBusy}
                                                    onClick={() =>
                                                        openSms(content)
                                                    }
                                                >
                                                    {t("contents.share.send")}
                                                </DBButton>
                                                <div className="contents-card__link-row">
                                                    <a
                                                        className="contents-card__link"
                                                        href={url || undefined}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title={url}
                                                        dir="ltr"
                                                    >
                                                        {url ||
                                                            (linkBusy
                                                                ? t(
                                                                      "contents.share.making"
                                                                  )
                                                                : t(
                                                                      "contents.share.linkEmpty"
                                                                  ))}
                                                    </a>
                                                    <button
                                                        type="button"
                                                        className="contents-card__icon-btn"
                                                        aria-label={t(
                                                            "contents.share.copy"
                                                        )}
                                                        title={t(
                                                            "contents.share.copy"
                                                        )}
                                                        disabled={
                                                            linkBusy && !url
                                                        }
                                                        onClick={() =>
                                                            copyLink(content)
                                                        }
                                                    >
                                                        <Copy
                                                            size={16}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="contents-card__native-share"
                                                        disabled={!url}
                                                        onClick={() =>
                                                            shareNative(content)
                                                        }
                                                    >
                                                        <Share
                                                            size={16}
                                                            aria-hidden="true"
                                                        />
                                                        <span>
                                                            {t(
                                                                "contents.share.native"
                                                            )}
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div className={"floating-notification" + (copied ? " show" : "")}>
                {t("copied")}
            </div>
            <FeedbackAuthModal
                show={showAuth}
                onHide={() => {
                    pendingRef.current = null;
                    setShowAuth(false);
                }}
                onSuccess={handleAuthSuccess}
            />
            <ShareSmsModal
                show={showSms}
                onHide={() => setShowSms(false)}
                preview={
                    openId
                        ? smsPreview(
                              contents.find((row) => row.id === openId) || {
                                  name: "",
                              }
                          )
                        : ""
                }
                loading={smsBusy}
                error={smsError}
                onSend={(phones) => {
                    const content = contents.find((row) => row.id === openId);
                    if (content) sendSms(content, phones);
                }}
            />
        </div>
    );
}
